import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ChefHat, Download, Eye, Layers, FileSpreadsheet, FileText, X } from 'lucide-react';
import { downloadRecipeExport } from '../components/recipes/recipeExport';

const fmtINR = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function RecipeViewDialog({ open, onClose, recipe }) {
  if (!recipe) return null;
  const isWeight = recipe.output_type === 'weight';
  const outputStr = isWeight
    ? `${((recipe.output_kg || 0) + (recipe.output_grams || 0) / 1000).toFixed(3)} kg`
    : `${recipe.output_portions || 0} portion${(recipe.output_portions || 0) === 1 ? '' : 's'}`;
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manager-recipe-view">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-[color:var(--color-primary)]" />
            {recipe.is_sub_recipe && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#1D4ED8] bg-[#1D4ED8]/10 border border-[#1D4ED8]/20 px-1.5 py-0.5 rounded uppercase tracking-wider">
                <Layers className="w-2.5 h-2.5" /> Sub-recipe
              </span>
            )}
            {recipe.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5C6B64]">
            Output: {outputStr} · {recipe.ingredients?.length || 0} ingredient{(recipe.ingredients?.length || 0) === 1 ? '' : 's'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="overflow-x-auto rounded-md border border-[#E2E8DF]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-b border-[#E2E8DF]">
                  <th className="px-3 py-2 text-left">Ingredient</th>
                  <th className="px-3 py-2 text-center">Type</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-center">Yield %</th>
                  <th className="px-3 py-2 text-right">Unit ₹</th>
                  <th className="px-3 py-2 text-right">Effective ₹</th>
                </tr>
              </thead>
              <tbody>
                {(recipe.ingredients || []).map((ln, i) => (
                  <tr key={i} className="border-b border-[#E2E8DF]/60">
                    <td className="px-3 py-2 text-[#1A2421]">{ln.ingredient_name}</td>
                    <td className="px-3 py-2 text-center">
                      {ln.type === 'sub_recipe' ? (
                        <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#1D4ED8] bg-[#1D4ED8]/10 px-1.5 py-0.5 rounded uppercase">
                          <Layers className="w-2.5 h-2.5" /> Sub
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#5C6B64] uppercase">Raw</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-xs tabular-nums text-[#1A2421]">
                      {(ln.qty_kg || 0) > 0 && `${ln.qty_kg} kg`}
                      {(ln.qty_kg || 0) > 0 && (ln.qty_grams || 0) > 0 && ' + '}
                      {(ln.qty_grams || 0) > 0 && `${ln.qty_grams} g`}
                      {(!ln.qty_kg && !ln.qty_grams) && '—'}
                    </td>
                    <td className="px-3 py-2 text-center text-xs tabular-nums">{ln.yield_pct}%</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">{fmtINR(ln.purchase_price)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[color:var(--color-primary)] tabular-nums">{fmtINR(ln.effective_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Card className="border-[color:var(--color-primary)]/20 bg-[color:var(--color-primary)]/[0.04]">
            <CardContent className="px-4 py-3 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Total recipe cost</p>
                <p className="text-xl font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="view-total-cost">{fmtINR(recipe.total_cost)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">{isWeight ? 'Cost / kg' : 'Cost / portion'}</p>
                <p className="text-xl font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="view-cost-per-unit">
                  {fmtINR(isWeight ? recipe.cost_per_kg : recipe.cost_per_portion)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerRecipes() {
  const { getHeaders, API } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/manager/recipes`, { headers: getHeaders() });
      setRecipes(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openView = async (row) => {
    try {
      const { data } = await axios.get(`${API}/manager/recipes/${row.id}`, { headers: getHeaders() });
      setViewing(data);
    } catch {
      toast.error('Could not load recipe');
    }
  };

  return (
    <div data-testid="manager-recipes-page">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Recipes</h2>
        <p className="text-sm text-[#5C6B64] mt-1">Recipes assigned to your outlet · View & export only</p>
      </div>

      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="px-0 pb-0 pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : recipes.length === 0 ? (
            <div className="py-12 text-center">
              <ChefHat className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B64]">No recipes assigned to this outlet yet</p>
              <p className="text-xs text-[#5C6B64] mt-1">Ask your admin to assign recipes from the Recipe Costing module.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-y border-[#E2E8DF] sticky top-0 z-[1]">
                    <th className="px-4 py-2 text-left">Recipe</th>
                    <th className="px-4 py-2 text-center">Type</th>
                    <th className="px-4 py-2 text-center">Lines</th>
                    <th className="px-4 py-2 text-right">Total cost</th>
                    <th className="px-4 py-2 text-right">Cost / unit</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.map(r => (
                    <tr key={r.id} className="border-b border-[#E2E8DF]/60 hover:bg-[#F9F9F8]"
                      data-testid={`manager-recipe-row-${r.id}`}>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5">
                          {r.is_sub_recipe && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#1D4ED8] bg-[#1D4ED8]/10 border border-[#1D4ED8]/20 px-1.5 py-0.5 rounded uppercase">
                              <Layers className="w-2.5 h-2.5" /> Sub
                            </span>
                          )}
                          <span className="font-medium text-[#1A2421]">{r.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center text-[11px] text-[#5C6B64] uppercase tracking-wider">
                        {r.output_type === 'weight'
                          ? `${((r.output_kg || 0) + (r.output_grams || 0) / 1000).toFixed(3)} kg`
                          : `${r.output_portions || 0} portion${(r.output_portions || 0) === 1 ? '' : 's'}`}
                      </td>
                      <td className="px-4 py-2 text-center text-xs text-[#5C6B64]">{r.ingredient_count}</td>
                      <td className="px-4 py-2 text-right font-semibold text-[#1A2421] tabular-nums">{fmtINR(r.total_cost)}</td>
                      <td className="px-4 py-2 text-right font-bold text-[color:var(--color-primary)] tabular-nums">
                        {r.output_type === 'weight' ? `${fmtINR(r.cost_per_kg)}/kg` : `${fmtINR(r.cost_per_portion)}/portion`}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => openView(r)}
                            className="h-8"
                            data-testid={`manager-recipe-view-${r.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8"
                                data-testid={`manager-recipe-export-${r.id}`}>
                                <Download className="w-3.5 h-3.5 mr-1" /> Export
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'xlsx' })}>
                                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel (.xlsx)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'pdf' })}>
                                <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {viewing && (
        <RecipeViewDialog open onClose={() => setViewing(null)} recipe={viewing} />
      )}
    </div>
  );
}
